import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useGameStore } from '../store/gameStore'
import { assetUrl } from '../utils/assets'

const CLICKABLE_SELECTOR = [
  'button',
  'input[type="radio"]',
  'input[type="checkbox"]',
  '.btn',
  '.lobby-start',
  '.pass',
  '.chat-send',
  '.donating-submit',
  '.donating-cancel',
  '.donation-receiving-accept',
  '.donation-receiving-reject',
  '.colony-collapse-event-player-offer-submit',
  '.queen-trading-close',
  '.queen-trading-player-offer-resource',
  '.season-overview-close',
].join(', ')

const GAME_TRACK_FILES = ['background-1.mp3', 'background-2.mp3', 'background-3.mp3']

export function useAudio() {
  const {
    screen,
    showGameMenu,
    soundEnabled,
    musicEnabled,
    soundVolume,
    musicVolume,
  } = useGameStore()

  const themeRef = useRef<HTMLAudioElement | null>(null)
  const clickRef = useRef<HTMLAudioElement | null>(null)
  const gameTracksRef = useRef<HTMLAudioElement[]>([])
  const currentTrackIdxRef = useRef(0)
  const interactionUnlockedRef = useRef(false)

  const shouldPlayTheme = screen !== 'game' || showGameMenu
  const shouldPlayGame = screen === 'game' && !showGameMenu

  // Keep refs in sync so onended callbacks see live values without being recreated
  const shouldPlayGameRef = useRef(shouldPlayGame)
  const musicEnabledRef = useRef(musicEnabled)
  const musicVolumeRef = useRef(musicVolume)
  shouldPlayGameRef.current = shouldPlayGame
  musicEnabledRef.current = musicEnabled
  musicVolumeRef.current = musicVolume

  const themeUrl = useMemo(() => assetUrl('sounds', 'colony-collapse-theme.mp3'), [])
  const clickUrl = useMemo(() => assetUrl('sounds', 'mouseclick.mp3'), [])
  const gameTrackUrls = useMemo(
    () => GAME_TRACK_FILES.map(f => assetUrl('sounds', f)),
    []
  )

  // Init theme + click sound (stable urls → runs once)
  useEffect(() => {
    const theme = new Audio(themeUrl)
    theme.loop = true
    theme.preload = 'auto'
    theme.crossOrigin = 'anonymous'
    themeRef.current = theme

    const click = new Audio(clickUrl)
    click.preload = 'auto'
    click.crossOrigin = 'anonymous'
    clickRef.current = click

    return () => {
      theme.pause()
      themeRef.current = null
      clickRef.current = null
    }
  }, [clickUrl, themeUrl])

  // Init game tracks + wire onended cycling (runs once)
  useEffect(() => {
    const tracks = gameTrackUrls.map(url => {
      const audio = new Audio(url)
      audio.preload = 'auto'
      audio.crossOrigin = 'anonymous'
      return audio
    })
    gameTracksRef.current = tracks

    tracks.forEach((track, idx) => {
      track.addEventListener('ended', () => {
        if (!shouldPlayGameRef.current || !musicEnabledRef.current || !interactionUnlockedRef.current) return
        const nextIdx = (idx + 1) % tracks.length
        currentTrackIdxRef.current = nextIdx
        const next = tracks[nextIdx]
        next.currentTime = 0
        next.volume = musicVolumeRef.current
        next.muted = false
        void next.play().catch(() => {})
      })
    })

    return () => {
      tracks.forEach(t => t.pause())
      gameTracksRef.current = []
    }
  }, [gameTrackUrls])

  // Sync volume + mute across all elements
  useEffect(() => {
    if (themeRef.current) {
      themeRef.current.muted = !musicEnabled
      themeRef.current.volume = musicVolume
    }
    if (clickRef.current) {
      clickRef.current.muted = !soundEnabled
      clickRef.current.volume = soundVolume
    }
    gameTracksRef.current.forEach(t => {
      t.muted = !musicEnabled
      t.volume = musicVolume
    })
  }, [musicEnabled, musicVolume, soundEnabled, soundVolume])

  // Play/pause lobby theme
  useEffect(() => {
    const theme = themeRef.current
    if (!theme) return
    if (interactionUnlockedRef.current && shouldPlayTheme && musicEnabled) {
      void theme.play().catch(() => {})
    } else {
      theme.pause()
    }
  }, [musicEnabled, shouldPlayTheme])

  // Play/pause game tracks — pick a fresh random track each time game becomes active
  useEffect(() => {
    const tracks = gameTracksRef.current
    if (!tracks.length) return

    if (shouldPlayGame && musicEnabled) {
      const startIdx = Math.floor(Math.random() * tracks.length)
      currentTrackIdxRef.current = startIdx
      if (interactionUnlockedRef.current) {
        tracks.forEach((t, i) => { if (i !== startIdx) t.pause() })
        const current = tracks[startIdx]
        current.currentTime = 0
        void current.play().catch(() => {})
      }
      // if not yet unlocked, unlock handler will start the track on first interaction
    } else {
      tracks.forEach(t => t.pause())
    }
  }, [shouldPlayGame, musicEnabled])

  // Interaction unlock + click sounds
  useEffect(() => {
    const unlockAudio = () => {
      interactionUnlockedRef.current = true

      const theme = themeRef.current
      if (theme && shouldPlayTheme && musicEnabled && theme.paused) {
        void theme.play().catch(() => {})
      }

      const tracks = gameTracksRef.current
      if (shouldPlayGame && musicEnabled && tracks.length) {
        const current = tracks[currentTrackIdxRef.current]
        if (current && current.paused) {
          tracks.forEach((t, i) => { if (i !== currentTrackIdxRef.current) t.pause() })
          current.currentTime = 0
          void current.play().catch(() => {})
        }
      }
    }

    const onPointerDown = () => { unlockAudio() }

    const onClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return
      const clickable = target.closest(CLICKABLE_SELECTOR)
      if (!clickable || clickable.closest('.disabled,[disabled]')) return

      unlockAudio()

      const click = clickRef.current
      if (click && soundEnabled) {
        click.currentTime = 0
        void click.play().catch(() => {})
      }
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('click', onClick, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('click', onClick, true)
    }
  }, [musicEnabled, shouldPlayTheme, shouldPlayGame, soundEnabled])
}

const BUZZ_FADE_MS = 200
const BUZZ_FADE_STEPS = 20

export function useBeeBuzz() {
  const { soundEnabled, soundVolume } = useGameStore()
  const soundEnabledRef = useRef(soundEnabled)
  const soundVolumeRef = useRef(soundVolume)
  const buzzRef = useRef<HTMLAudioElement | null>(null)
  const fadeIntervalRef = useRef<number | null>(null)
  const fadeTimeoutRef = useRef<number | null>(null)
  const buzzUrl = useMemo(() => assetUrl('sounds', 'bee-buzz.mp3'), [])

  useEffect(() => {
    soundEnabledRef.current = soundEnabled
    soundVolumeRef.current = soundVolume
  }, [soundEnabled, soundVolume])

  useEffect(() => {
    const buzz = new Audio(buzzUrl)
    buzz.preload = 'auto'
    buzz.crossOrigin = 'anonymous'
    buzzRef.current = buzz
    return () => {
      buzz.pause()
      buzzRef.current = null
    }
  }, [buzzUrl])

  return useCallback((duration: number) => {
    const buzz = buzzRef.current
    if (!buzz || !soundEnabledRef.current) return

    if (fadeIntervalRef.current !== null) {
      window.clearInterval(fadeIntervalRef.current)
      fadeIntervalRef.current = null
    }
    if (fadeTimeoutRef.current !== null) {
      window.clearTimeout(fadeTimeoutRef.current)
      fadeTimeoutRef.current = null
    }

    const vol = soundVolumeRef.current
    buzz.currentTime = 0
    buzz.volume = vol
    void buzz.play().catch(() => {})

    const stepMs = BUZZ_FADE_MS / BUZZ_FADE_STEPS

    fadeTimeoutRef.current = window.setTimeout(() => {
      fadeTimeoutRef.current = null
      let step = 0
      fadeIntervalRef.current = window.setInterval(() => {
        step++
        buzz.volume = Math.max(0, vol * (1 - step / BUZZ_FADE_STEPS))
        if (step >= BUZZ_FADE_STEPS) {
          buzz.pause()
          window.clearInterval(fadeIntervalRef.current!)
          fadeIntervalRef.current = null
        }
      }, stepMs)
    }, Math.max(0, duration - BUZZ_FADE_MS))
  }, [])
}
